import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { fetchProviderSignupCountries } from "./provider-signup-countries";
import type { SupabaseAuth } from "./supabase-auth-client";
import {
  hasAuthCallbackInUrl,
  hasStoredSupabaseSession,
  supabaseAuthStorageKey
} from "./supabase-session-hint";
import {
  clearRetainedDeviceState,
  releaseAccountDeviceState,
  releaseDeviceStateWithoutSession,
  retainAccountDeviceStateOnNextSignOut
} from "./account-device-state";
import { setSignOutPending } from "./sign-out-state";

import {
  CURRENT_LEGAL_VERSIONS,
  type SignupLegalConsentMetadata
} from "@tinkr/shared/legal";
import {
  isLegalSignupBlocked,
  LEGAL_SIGNUP_UNAVAILABLE_MESSAGE
} from "./legal-publication";

async function requireProviderSignupCountry(countryCode: string): Promise<void> {
  let countries: string[];
  try {
    countries = await fetchProviderSignupCountries();
  } catch {
    throw new Error("provider_signup_country_check_failed");
  }
  if (!countries.includes(countryCode.trim().toUpperCase())) {
    throw new Error("provider_signup_country_not_supported");
  }
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabasePublishableKey = import.meta.env
  .VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

function isHttpUrl(value: string | undefined): value is string {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export const hasSupabaseConfig = Boolean(
  isHttpUrl(supabaseUrl) && supabasePublishableKey
);

const useE2eAuthStub = import.meta.env.VITE_E2E_AUTH_STUB === "true";

// ADR-WES1: the auth client (./supabase-auth-client, the same AuthClient
// createClient builds) is its own chunk. It loads when this page needs it: a
// persisted session or pending PKCE verifier, an auth callback in the URL, an
// auth action, or a session appearing from another tab. Anonymous visitors on
// the public screens never download it.
const authEnabled = hasSupabaseConfig && !useE2eAuthStub;
const authStorageKey = authEnabled ? supabaseAuthStorageKey(supabaseUrl as string) : null;
let authClient: Promise<SupabaseAuth> | null = null;
const waitingSubscribers = new Set<(client: SupabaseAuth) => void>();

function loadSupabaseAuth(): Promise<SupabaseAuth | null> {
  if (!authEnabled) return Promise.resolve(null);
  authClient ??= import("./supabase-auth-client").then(
    ({ createSupabaseAuth }) => {
      const client = createSupabaseAuth(supabaseUrl as string, supabasePublishableKey as string);
      // Subscribers attach before any caller can issue an auth request, so
      // no SIGNED_IN / TOKEN_REFRESHED event is missed.
      for (const attach of waitingSubscribers) attach(client);
      waitingSubscribers.clear();
      return client;
    },
    (error: unknown) => {
      authClient = null;
      throw error;
    }
  );
  return authClient;
}

/** True when this page load needs the auth client before it can know the session. */
export function needsSupabaseAuthAtBoot(): boolean {
  if (!authEnabled || typeof window === "undefined") return false;
  return hasStoredSupabaseSession(authStorageKey) || hasAuthCallbackInUrl(window.location.href);
}

/** Start downloading the auth client without waiting for it (e.g. a signed-in boot). */
export function preloadSupabaseAuth(): void {
  void loadSupabaseAuth().catch(() => {
    // The caller that needs it retries and reports the failure.
  });
}

/**
 * Subscribe to auth state changes. The subscription attaches when the client
 * loads; until then a session written by another tab loads it, which emits
 * INITIAL_SESSION with that session. Returns an unsubscribe function.
 */
export function subscribeToAuthChanges(
  callback: (event: AuthChangeEvent, session: Session | null) => void
): () => void {
  if (!authEnabled || typeof window === "undefined") return () => {};
  let cancelled = false;
  let unsubscribe: (() => void) | null = null;
  const attach = (client: SupabaseAuth) => {
    if (cancelled) return;
    const { data } = client.auth.onAuthStateChange(callback);
    unsubscribe = () => data.subscription.unsubscribe();
  };
  const queue = () => {
    waitingSubscribers.add(attach);
    // A load that started earlier may have failed: whoever loads next attaches.
  };
  if (authClient) {
    authClient.then(attach, queue);
  } else {
    queue();
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key === authStorageKey && event.newValue !== null && !authClient) preloadSupabaseAuth();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    cancelled = true;
    waitingSubscribers.delete(attach);
    window.removeEventListener("storage", onStorage);
    unsubscribe?.();
  };
}

function authCallbackType(
  url: URL,
  hashParams?: URLSearchParams
): string | null {
  return url.searchParams.get("type") ?? hashParams?.get("type") ?? null;
}

function scrubAuthCallbackUrl(url: URL, callbackType: string | null): void {
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  url.searchParams.delete("type");

  if (callbackType === "invite") {
    url.searchParams.set("screen", "auth");
    url.searchParams.set("mode", "invite");
  }
}

function readE2eSession(): Session | null {
  if (
    import.meta.env.VITE_E2E_AUTH_STUB !== "true" ||
    typeof window === "undefined"
  ) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem("tinkr.e2e.session");
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export async function consumeAuthCallback(): Promise<Session | null> {
  if (!authEnabled || typeof window === "undefined" || !hasAuthCallbackInUrl(window.location.href)) {
    return null;
  }
  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    return null;
  }

  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  if (code) {
    const callbackType = authCallbackType(url);
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) {
      throw error;
    }

    scrubAuthCallbackUrl(url, callbackType);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    return data.session;
  }

  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  const accessToken = hashParams.get("access_token");
  const refreshToken = hashParams.get("refresh_token");
  if (accessToken && refreshToken) {
    const callbackType = authCallbackType(url, hashParams);
    scrubAuthCallbackUrl(url, callbackType);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken
    });
    if (error) {
      throw error;
    }

    return data.session;
  }

  return null;
}

export async function getCurrentSession(): Promise<Session | null> {
  const e2eSession = readE2eSession();
  if (e2eSession) {
    return e2eSession;
  }

  if (!authEnabled) {
    return null;
  }
  if (!authClient && !hasStoredSupabaseSession(authStorageKey)) {
    // Nothing is stored for this browser, so the auth client (ADR-WES1) is
    // never loaded; the start-up still has to release instant alerts an
    // account left here, whose session ended while no console was open
    // (ADR-WOA1, WOA1-R2). A refresh that fails offline reports an error
    // below instead of reaching this branch.
    releaseDeviceStateWithoutSession();
    return null;
  }
  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    return null;
  }

  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw error;
  }
  // No error and no session: nothing is stored for this browser (a refresh
  // that fails offline reports an error instead). Instant alerts an account
  // left here belong to a session that has ended (ADR-WOA1, WOA1-R2).
  if (!data.session) releaseDeviceStateWithoutSession();
  return data.session;
}

export async function sendProviderMagicLink(input: {
  email: string;
  companyName?: string;
  accountCountryCode?: string;
  mode: "login" | "signup";
}): Promise<void> {
  if (input.mode === "signup" && isLegalSignupBlocked()) {
    throw new Error(LEGAL_SIGNUP_UNAVAILABLE_MESSAGE);
  }
  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    throw new Error("Authentication is not available");
  }

  if (input.mode === "signup") await requireProviderSignupCountry(input.accountCountryCode ?? "");
  const { error } = await supabase.auth.signInWithOtp({
    email: input.email,
    options: {
      shouldCreateUser: input.mode === "signup",
      emailRedirectTo: window.location.origin,
      data:
        input.mode === "signup"
          ? {
              account_intent: "provider",
              company_name: input.companyName ?? "",
              country_code: input.accountCountryCode ?? ""
            }
          : undefined
    }
  });

  if (error) {
    throw error;
  }
}

export async function signUpProviderWithPassword(input: {
  email: string;
  password: string;
  companyName: string;
  accountCountryCode: string;
}): Promise<Session | null> {
  if (isLegalSignupBlocked()) {
    throw new Error(LEGAL_SIGNUP_UNAVAILABLE_MESSAGE);
  }
  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    throw new Error("Authentication is not available");
  }

  // Production reaches this point only with published documents. Local
  // development may stamp a draft version, but the API never treats a draft
  // id/version as a current acceptance. On first login the gate records only
  // metadata that exactly matches the database's published current rows.
  const legalConsent: SignupLegalConsentMetadata = {
    terms_of_service: CURRENT_LEGAL_VERSIONS.terms_of_service,
    privacy_policy: CURRENT_LEGAL_VERSIONS.privacy_policy,
    consented_at: new Date().toISOString()
  };

  await requireProviderSignupCountry(input.accountCountryCode);
  const { data, error } = await supabase.auth.signUp({
    email: input.email,
    password: input.password,
    options: {
      emailRedirectTo: window.location.origin,
      data: {
        account_intent: "provider",
        company_name: input.companyName,
        country_code: input.accountCountryCode,
        legal_accepted: legalConsent
      }
    }
  });

  if (error) {
    throw error;
  }
  return data.session;
}

export async function signInProviderWithPassword(input: {
  email: string;
  password: string;
}): Promise<Session | null> {
  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    throw new Error("Authentication is not available");
  }

  const { data, error } = await supabase.auth.signInWithPassword(input);
  if (error) {
    throw error;
  }
  return data.session;
}

export async function requestProviderPasswordReset(email: string): Promise<void> {
  if (useE2eAuthStub && typeof window !== "undefined") {
    window.localStorage.setItem("tinkr.e2e.passwordResetEmail", email);
    return;
  }

  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    throw new Error("Authentication is not available");
  }

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/?screen=auth&mode=reset`
  });

  if (error) {
    throw error;
  }
}

export async function updateProviderPassword(password: string): Promise<void> {
  if (useE2eAuthStub && typeof window !== "undefined") {
    window.localStorage.setItem(
      "tinkr.e2e.updatedPasswordLength",
      String(password.length)
    );
    return;
  }

  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    throw new Error("Authentication is not available");
  }

  const { error } = await supabase.auth.updateUser({ password });

  if (error) {
    throw error;
  }

  const { error: signOutError } = await supabase.auth.signOut({ scope: "others" });
  if (signOutError) {
    throw signOutError;
  }
}

export async function changeProviderPassword(input: {
  email: string;
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  if (useE2eAuthStub && typeof window !== "undefined") {
    window.localStorage.setItem("tinkr.e2e.changedPasswordEmail", input.email);
    window.localStorage.setItem(
      "tinkr.e2e.updatedPasswordLength",
      String(input.newPassword.length)
    );
    return;
  }

  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    throw new Error("Authentication is not available");
  }

  // Supabase does not expose a password-verification primitive. Re-signing in
  // is intentional, but it consumes the normal sign-in rate limit and emits a
  // harmless SIGNED_IN auth event for the already-authenticated user.
  const { error: signInError } = await supabase.auth.signInWithPassword({
    email: input.email,
    password: input.currentPassword
  });
  if (signInError) {
    throw signInError;
  }

  await updateProviderPassword(input.newPassword);
}

export async function resendProviderVerification(email: string): Promise<void> {
  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    throw new Error("Authentication is not available");
  }

  const { error } = await supabase.auth.resend({
    type: "signup",
    email,
    options: {
      emailRedirectTo: window.location.origin
    }
  });

  if (error) {
    throw error;
  }
}

// Dormant OAuth helpers for the future first-login metadata completion flow.
// Supabase handles the redirect dance once `signInWithOAuth` resolves, and
// `redirectTo` lands on the same callback path consumed by
// `consumeAuthCallback`. The active AuthScreen intentionally does not render
// these controls while provider signup depends on password-form metadata.
export async function signInWithGoogle(): Promise<void> {
  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    throw new Error("Authentication is not available");
  }
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: window.location.origin }
  });
  if (error) {
    throw error;
  }
}

export async function signInWithApple(): Promise<void> {
  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    throw new Error("Authentication is not available");
  }
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "apple",
    options: { redirectTo: window.location.origin }
  });
  if (error) {
    throw error;
  }
}

/**
 * Sign out of this browser. By default this also releases the account's
 * device state first (ADR-WOA1): onboarding drafts, and the
 * instant-alert push registration, removed server-side while the session can
 * still authorize it. The password-setup flow signs out only to make the user
 * sign in again with the new password on the same device, so it keeps them.
 *
 * One sign-out runs at a time: a second tap while the first is still releasing
 * device state joins it instead of starting another, and the sign-out buttons
 * show that it is working (useSignOutPending, PUX-I-1).
 */
let signOutInFlight: Promise<void> | null = null;

export function signOut(options: { releaseDeviceState?: boolean } = {}): Promise<void> {
  if (signOutInFlight) return signOutInFlight;
  setSignOutPending(true);
  const run: Promise<void> = performSignOut(options).finally(() => {
    if (signOutInFlight === run) signOutInFlight = null;
    setSignOutPending(false);
  });
  signOutInFlight = run;
  return run;
}

async function performSignOut(options: { releaseDeviceState?: boolean }): Promise<void> {
  if (
    import.meta.env.VITE_E2E_AUTH_STUB === "true" &&
    typeof window !== "undefined"
  ) {
    window.localStorage.removeItem("tinkr.e2e.session");
  }

  const supabase = await loadSupabaseAuth();
  if (!supabase) {
    return;
  }

  if (options.releaseDeviceState !== false) {
    try {
      const { data } = await supabase.auth.getSession();
      await releaseAccountDeviceState(data.session);
    } catch {
      // Never block sign-out on device cleanup.
    }
  } else {
    // The SIGNED_OUT event this sign-out emits must keep it too.
    retainAccountDeviceStateOnNextSignOut();
  }

  try {
    await supabase.auth.signOut();
  } finally {
    clearRetainedDeviceState();
  }
}
